import {Observable} from 'rxjs';
import {CpmCUserCreateApiSchema, CpmCUserUpdateApiSchema} from '../../schemas/c-global/cpmc-user.api-schemas';
import {CpmCUserApiModel} from '../../models/c-global/cpmc-user.api-model';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';
import {CpmCSdkRbacUserAllowedRouteResourcesApiSchema} from '../../schemas/cpmc-sdk-rbac.api-schemas';

export type CpmCGetUserListParams = {
    [K in keyof CpmCUserApiModel]?: CpmCUserApiModel[K] extends AllowedQueryParamTypes ? CpmCUserApiModel[K] : never;
} & {} & QueryParams;

/**
 * User Api Service
 */
export class CpmCUserApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'email', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl() {
        return CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/user';
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(): string {
        return `${CpmCUserApiService.baseUrl()}/list`;
    }
    public static endpointListByUUID(): string {
        return `${CpmCUserApiService.baseUrl()}/list-by-uuid`;
    }
    public static endpointNew(): string {
        return `${CpmCUserApiService.baseUrl()}`;
    }
    public static endpointGet(userUUID: string): string {
        return `${CpmCUserApiService.baseUrl()}/${userUUID}`;
    }
    public static endpointEdit(userUUID: string): string {
        return `${CpmCUserApiService.baseUrl()}/${userUUID}`;
    }
    public static endpointDelete(userUUID: string): string {
        return `${CpmCUserApiService.baseUrl()}/${userUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getUserList(
        params: CpmCGetUserListParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCUserApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCUserApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCUserApiModel>>(
            CpmCUserApiService.endpointList(),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getUserListByUUID(
        uuids: string[],
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCUserApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCUserApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCUserApiModel>>(
            CpmCUserApiService.endpointListByUUID(),
            {
                ...{'uuid(in)': uuids},
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getUserByUUID(userUUID: string): Observable<CpmCUserApiModel> {
        return CpmCApiBridge.httpGet<CpmCUserApiModel>(CpmCUserApiService.endpointGet(userUUID));
    }
    public static createUser(payload: CpmCUserCreateApiSchema): Observable<CpmCUserApiModel> {
        return CpmCApiBridge.httpPost<CpmCUserApiModel>(CpmCUserApiService.endpointNew(), payload);
    }
    public static updateUser(userUUID: string, payload: CpmCUserUpdateApiSchema): Observable<CpmCUserApiModel> {
        return CpmCApiBridge.httpPut<CpmCUserApiModel>(CpmCUserApiService.endpointEdit(userUUID), payload);
    }
    public static deleteUser(userUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(CpmCUserApiService.endpointDelete(userUUID));
    }

    // ===============================================================
    // ===============================================================

    public static getSelf(): Observable<CpmCUserApiModel> {
        return CpmCApiBridge.httpGet<CpmCUserApiModel>(`${CpmCUserApiService.baseUrl()}/self`);
    }
    public static getSelfAllowedRouteResources(): Observable<CpmCSdkRbacUserAllowedRouteResourcesApiSchema> {
        return CpmCApiBridge.httpGet<CpmCSdkRbacUserAllowedRouteResourcesApiSchema>(
            `${CpmCUserApiService.baseUrl()}/self/allowed-route-resources`
        );
    }
}
